import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useRefresh } from '../../contexts/RefreshContext';
import { apiClient } from '../../api/apiConfig';
import MentionInput from '../ui/MentionInput';
import PersonAutocomplete from '../ui/PersonAutocomplete';
import { Button, Input, Select, Checkbox, FormField, Alert, Spinner } from '../ui';
import { Task } from '../../types/task';
import { Category } from '../../types/category';
import { Project } from '../../types/project';
import { Business } from '../../types/business';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface TaskFormProps {
  isOpen: boolean;
  onClose: () => void;
  onTaskCreated: () => void;
  onTaskUpdated?: (updatedTask: Task) => void;
  initialTaskData?: Task | null;
  isEditMode?: boolean;
}

interface TaskFormData {
  description: string;
  due_date: string;
  project_id: string;
  business_id: string;
  category_ids: number[];
  responsible_ids: number[];
  impacted_ids: number[];
  top_priority: boolean;
  email_id: number | null;
}

const TaskForm: React.FC<TaskFormProps> = ({
  isOpen,
  onClose,
  onTaskCreated,
  onTaskUpdated,
  initialTaskData = null,
  isEditMode = false,
}) => {
  const { refreshTasks } = useRefresh();
  const [formData, setFormData] = useState<TaskFormData>({
    description: '',
    due_date: '',
    project_id: '',
    business_id: '',
    category_ids: [],
    responsible_ids: [],
    impacted_ids: [],
    top_priority: false,
    email_id: null,
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen) return;
      setIsLoadingData(true);
      setFetchError(null);
      try {
        const [catRes, projRes, bizRes] = await Promise.all([
          apiClient.get('/api/categories/?type=general'),
          apiClient.get('/api/projects/'),
          apiClient.get('/api/businesses/'),
        ]);
        setCategories(catRes.data || []);
        setProjects(projRes.data || []);
        setBusinesses(bizRes.data || []);
      } catch (err) {
        console.error("Error fetching form data:", err);
        setFetchError("Failed to load form data. Please try again.");
      } finally {
        setIsLoadingData(false);
      }
    };
    fetchData();
  }, [isOpen]);

  useEffect(() => {
    if (isEditMode && initialTaskData && isOpen) {
      setFormData({
        description: initialTaskData.description || '',
        due_date: initialTaskData.due_date || '',
        project_id: initialTaskData.project?.id?.toString() || '',
        business_id: initialTaskData.business?.id?.toString() || '',
        category_ids: initialTaskData.categories.map(cat => cat.id) || [],
        responsible_ids: initialTaskData.responsible.map(p => p.id) || [],
        impacted_ids: initialTaskData.impacted.map(p => p.id) || [],
        top_priority: initialTaskData.top_priority || false,
        email_id: initialTaskData.email || null,
      });
    } else if (!isEditMode && isOpen && initialTaskData) {
      // Create mode with prefill data (e.g., from email quick action)
      setFormData({
        description: initialTaskData.description || '',
        due_date: initialTaskData.due_date || '',
        project_id: initialTaskData.project?.id?.toString() || '',
        business_id: initialTaskData.business?.id?.toString() || '',
        category_ids: initialTaskData.categories?.map(cat => cat.id) || [],
        responsible_ids: initialTaskData.responsible?.map(p => p.id) || [],
        impacted_ids: initialTaskData.impacted?.map(p => p.id) || [],
        top_priority: initialTaskData.top_priority || false,
        email_id: initialTaskData.email || null,
      });
    } else if (!isEditMode && isOpen) {
      setFormData({
        description: '',
        due_date: '',
        project_id: '',
        business_id: '',
        category_ids: [],
        responsible_ids: [],
        impacted_ids: [],
        top_priority: false,
        email_id: null,
      });
    }
  }, [isEditMode, initialTaskData, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (name === 'project_id') {
      // Handle project selection with auto-inheritance
      handleProjectChange(value);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      }));
    }
  };

  const handleProjectChange = (projectId: string) => {
    const selectedProject = projects.find(p => p.id.toString() === projectId);

    setFormData(prev => {
      let updatedData = {
        ...prev,
        project_id: projectId
      };

      if (selectedProject) {
        // Auto-inherit business from project if no business is currently selected
        if (!prev.business_id && selectedProject.business) {
          updatedData.business_id = selectedProject.business.id.toString();
        }

        // Auto-inherit categories from project (merge with existing selections)
        if (selectedProject.categories && selectedProject.categories.length > 0) {
          const projectCategoryIds = selectedProject.categories.map(cat => cat.id);
          const existingCategoryIds = prev.category_ids;
          // Merge and remove duplicates using filter approach
          const allCategoryIds = [...existingCategoryIds, ...projectCategoryIds];
          const mergedCategoryIds = allCategoryIds.filter((id, index) => allCategoryIds.indexOf(id) === index);
          updatedData.category_ids = mergedCategoryIds;
        }
      } else if (projectId === '') {
        // Project deselected - only clear auto-inherited values if they came from a project
        // This is a simplified approach - in practice you might want to track which values were auto-inherited
        // For now, we'll leave the business and categories as they are when project is deselected
      }

      return updatedData;
    });
  };

  const handleMultiSelectChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'category_ids') => {
    const id = parseInt(e.target.value);
    const isChecked = e.target.checked;
    setFormData(prev => ({
      ...prev,
      [field]: isChecked
        ? [...prev[field], id]
        : prev[field].filter(existingId => existingId !== id)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    const payload = {
      description: formData.description,
      due_date: formData.due_date || null,
      project_id: formData.project_id ? parseInt(formData.project_id) : null,
      business_id: formData.business_id ? parseInt(formData.business_id) : null,
      category_ids: formData.category_ids,
      responsible_ids: formData.responsible_ids,
      impacted_ids: formData.impacted_ids,
      top_priority: formData.top_priority,
      email_id: formData.email_id,
    };

    try {
      let response;
      if (isEditMode && initialTaskData?.id) {
        response = await apiClient.patch(`/api/tasks/${initialTaskData.id}/`, payload);
      } else {
        response = await apiClient.post('/api/tasks/', payload);
      }

      const responseData = response.data;
      setSubmitSuccess(true);
      if (isEditMode && onTaskUpdated) onTaskUpdated(responseData);
      else onTaskCreated();
      refreshTasks();
      setTimeout(() => onClose(), 1500);
    } catch (err: unknown) {
      let errorMessage = `Failed to ${isEditMode ? 'update' : 'create'} task. Please try again.`;

      if (err instanceof Error) {
        errorMessage = err.message;
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    if (!isSubmitting) onClose();
  }, [isSubmitting, onClose]);

  const [closeButtonHovered, setCloseButtonHovered] = useState(false);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
          }} />
        </Transition.Child>

        <div style={{
          position: 'fixed',
          inset: 0,
          overflowY: 'auto',
        }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16],
            textAlign: 'center',
          }}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel style={{
                width: '100%',
                maxWidth: '28rem',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: '16px',
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                verticalAlign: 'middle',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: 'all',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: SPACING[16],
                }}>
                  <Dialog.Title as="h3" style={{
                    fontSize: TYPOGRAPHY.size['2xl'],
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary,
                  }}>
                    {isEditMode ? 'Edit Task' : 'Create New Task'}
                  </Dialog.Title>
                  <button
                    type="button"
                    style={{
                      color: closeButtonHovered ? COLORS.text.secondary : COLORS.text.tertiary,
                      opacity: isSubmitting ? 0.5 : 1,
                      background: 'none',
                      border: 'none',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      padding: 0,
                    }}
                    onMouseEnter={() => setCloseButtonHovered(true)}
                    onMouseLeave={() => setCloseButtonHovered(false)}
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    <XMarkIcon style={{ height: '24px', width: '24px' }} />
                  </button>
                </div>

                {fetchError && (
                  <Alert variant="error" style={{ marginBottom: SPACING[16] }}>
                    {fetchError}
                  </Alert>
                )}

                {isLoadingData ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '10rem',
                  }}>
                    <Spinner size="md" />
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                    <div>
                      <label htmlFor="description" style={{
                        display: 'block',
                        fontSize: TYPOGRAPHY.size.sm,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        color: COLORS.text.secondary,
                      }}>
                        Description *
                      </label>
                      <MentionInput
                        value={formData.description}
                        onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                        placeholder="Type @ to mention someone..."
                        rows={3}
                      />
                    </div>


                    <FormField label="Due Date (Optional)" htmlFor="due_date">
                      <Input
                        type="date"
                        id="due_date"
                        name="due_date"
                        value={formData.due_date}
                        onChange={handleInputChange}
                      />
                    </FormField>

                    <FormField label="Project (Optional)" htmlFor="project_id">
                      <Select
                        id="project_id"
                        name="project_id"
                        value={formData.project_id}
                        onChange={handleInputChange}
                      >
                        <option value="">No Project</option>
                        {projects.map((proj) => (
                          <option key={proj.id} value={proj.id}>
                            {proj.name}
                          </option>
                        ))}
                      </Select>
                      {formData.project_id && (
                        <p style={{
                          marginTop: SPACING[4],
                          fontSize: TYPOGRAPHY.size.sm,
                          color: COLORS.accent.default,
                        }}>
                          Business and categories are auto-inherited from this project
                        </p>
                      )}
                    </FormField>

                    {!formData.project_id && (
                      <FormField label="Business (Optional)" htmlFor="business_id">
                        <Select
                          id="business_id"
                          name="business_id"
                          value={formData.business_id}
                          onChange={handleInputChange}
                        >
                          <option value="">No Business</option>
                          {businesses.map((biz) => (
                            <option key={biz.id} value={biz.id}>
                              {biz.name}
                            </option>
                          ))}
                        </Select>
                      </FormField>
                    )}

                    {!formData.project_id && (
                      <FormField label="Categories (Optional)">
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: SPACING[8],
                          maxHeight: '8rem',
                          overflowY: 'auto',
                          border: `1px solid ${COLORS.border.default}`,
                          borderRadius: RADIUS,
                          padding: SPACING[8],
                        }}>
                          {categories.map((category) => (
                            <Checkbox
                              key={category.id}
                              id={`category-${category.id}`}
                              label={category.name}
                              checked={formData.category_ids.includes(category.id)}
                              onChange={(e) => handleMultiSelectChange(e, 'category_ids')}
                              value={category.id}
                            />
                          ))}
                        </div>
                      </FormField>
                    )}

                    <PersonAutocomplete
                      selectedPersonIds={formData.responsible_ids}
                      onChange={(selectedPersonIds) => setFormData(prev => ({ ...prev, responsible_ids: selectedPersonIds }))}
                      label="Responsible People (Optional)"
                      placeholder="Type to search for responsible people..."
                    />

                    <PersonAutocomplete
                      selectedPersonIds={formData.impacted_ids}
                      onChange={(selectedPersonIds) => setFormData(prev => ({ ...prev, impacted_ids: selectedPersonIds }))}
                      label="Impacted People (Optional)"
                      placeholder="Type to search for impacted people..."
                    />

                    <FormField label="Priority">
                      <Checkbox
                        id="top_priority"
                        label="Top Priority (show prominently on dashboard)"
                        checked={formData.top_priority}
                        onChange={(e) => setFormData(prev => ({ ...prev, top_priority: e.target.checked }))}
                      />
                    </FormField>

                    {submitSuccess && (
                      <Alert variant="success">
                        {isEditMode ? 'Task updated successfully!' : 'Task created successfully!'}
                      </Alert>
                    )}

                    {submitError && (
                      <Alert variant="error">
                        {submitError}
                      </Alert>
                    )}

                    <div style={{
                      marginTop: SPACING[24],
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: SPACING[12],
                    }}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        isLoading={isSubmitting}
                        loadingText={isEditMode ? 'Updating...' : 'Creating...'}
                        disabled={isLoadingData || fetchError !== null}
                      >
                        {isEditMode ? 'Update Task' : 'Create Task'}
                      </Button>
                    </div>
                  </form>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TaskForm;
